import { useCallback, useEffect, useState } from 'react';
import type React from 'react';

import { Button } from '@change/ds-core-ui';
import { SignaturesIcon } from '@change/ds-icons';

import { Translate } from '@change-corgi/core-react/i18n';
import { ClientRender } from '@change-corgi/core-react/ssr/render';

import { usePetitionDetailsPageData, usePetitionDetailsPageUserData } from '#src/app/pages/petitionDetails/pageContext';
import {
	useMobileNativeShare,
	useShareCtaPlacementEnabled,
	useSignatureTracking,
} from '#src/app/pages/petitionDetails/shared/hooks';
import { useTrackExperimentExposure } from '#src/app/shared/hooks/tracking';

import { ActionButtonWrapper } from './ActionButtonWrapper';
import type { ActionPanelVariant } from './MobileActionButton';
import { MobileSignModal, usePreloadMobileSignModal } from './MobileSignModal';
import { useMobileModalContext } from './shared/useMobileModalContext';
import { useShowSignForm } from './shared/useShowSignForm';
import { SignCtaShareAffordance } from './SignCtaShareAffordance';

type Props = {
	/**
	 * Whether this render site paints the share affordance. Distinct from `ownsShareExposure`: under
	 * `inline-then-sticky` both sites paint it and only the in-flow one reports exposure.
	 */
	rendersShareAffordance?: boolean;
	/**
	 * Whether this render site is the one that reports share-cta-copy `$exposure` for the current
	 * layout. Passed down rather than derived from `sticky`, because which site owns it varies by
	 * layout — see `ownsShareExposure` in MobileActionButton.
	 */
	ownsShareExposure: boolean;
	sticky: boolean;
	visible: boolean;
	variant: ActionPanelVariant;
};

/**
 * `shareCtaPlacementEnabled` already folds in `rendersShareAffordance` (see below), so it's not
 * re-checked here.
 *
 * `ownsShareExposure` is passed in rather than derived from `sticky`. Both render sites mount this
 * component, so at most one of them may fire share-cta-copy's `$exposure` — and which one that is
 * depends on the layout, which only the caller knows.
 *
 * `visible` is checked on top, because `ActionButtonWrapper` renders `null` for an in-flow instance
 * with `visible: false` — the affordance is then never on screen, so exposure would enrol a viewer
 * who cannot convert on it.
 */
function shouldTrackShareCtaCopyExperiment(
	ownsShareExposure: boolean,
	visible: boolean,
	hasShareAction: boolean,
	shareCtaPlacementEnabled: boolean,
) {
	return ownsShareExposure && visible && hasShareAction && shareCtaPlacementEnabled;
}

/**
 * Always stacks the share affordance's slot under the sign button, regardless of
 * `shareCtaPlacementEnabled` — with a single child, `gap-2` adds no spacing, so this is visually
 * identical to the bare button. `shareCtaPlacementEnabled` depends on `useIsMobile()`, which reports
 * `false` server-side on the guest, cached petition route (no session, so no real user agent to read)
 * and the real device type client-side — a wrapper that appeared only when that flag is true would
 * flip in and out across hydration and mismatch. See CHANGE-99549.
 */
function pickActionContent({
	shareAffordance,
	signButton,
}: {
	shareAffordance: React.ReactNode;
	signButton: React.ReactNode;
}): React.ReactNode {
	return (
		<div className="flex flex-col gap-2">
			{signButton}
			{shareAffordance}
		</div>
	);
}

export const MobileSignButtonContainer: React.FC<Props> = ({
	rendersShareAffordance = true,
	ownsShareExposure,
	sticky,
	visible,
	variant,
}) => {
	const [isOpen, setIsOpen] = useState(false);
	const { trackMobileSignIntent } = useSignatureTracking();

	const context = useMobileModalContext();

	usePreloadMobileSignModal();

	const {
		data: {
			experiments: { petitionDetailsShareCtaCopyExperiment },
		},
	} = usePetitionDetailsPageUserData();
	const {
		data: {
			petition: { id: petitionId },
		},
	} = usePetitionDetailsPageData();

	// `sticky` only names the tracking context here; it does not decide whether this instance renders
	// the affordance. That is `ownsShareExposure`, which the layout derives.
	const trackingContext = sticky ? 'petition_show_sign_cta_sticky' : 'petition_show_sign_cta_inline';

	// Evaluated in every arm, control included: this is the availability half of the render/exposure
	// predicate, and it must be arm-independent so the compared populations are identical.
	const shareAction = useMobileNativeShare(trackingContext);
	const shareCtaPlacementEnabled = useShareCtaPlacementEnabled() && rendersShareAffordance;
	const trackShareCtaCopyExperiment = shouldTrackShareCtaCopyExperiment(
		ownsShareExposure,
		visible,
		!!shareAction,
		shareCtaPlacementEnabled,
	);
	// resetKeys is required, not optional: this component instance persists across an SPA
	// petition-to-petition navigation (no remount), and variation/experimentName are otherwise
	// unchanged, so without the petition id the new petition's legitimate exposure would be deduped
	// against the previous one. Same requirement as useTreatSignFormAddressRemovalExperiment on this page.
	const trackShareCtaCopyExposure = useTrackExperimentExposure(petitionDetailsShareCtaCopyExperiment, {
		resetKeys: [petitionId],
	});

	// Fired from a stable effect on this component instance, not a conditionally-mounted
	// <TrackExperimentExposure>: `shareAction` resolves asynchronously (see useMobileNativeShare,
	// which queries on `sessionUuid`) and can flicker unavailable → available after mount. A
	// conditionally-mounted tracker would unmount/remount on that flicker, resetting its per-mount
	// dedup flag (useTrackOnce) and double-firing $exposure — this component instance persists across
	// the flicker, so calling the hook's returned function here keeps the dedup flag stable instead.
	// Both instances of MobileActionButtonContainer render this component; `ownsShareExposure` in
	// shouldTrackShareCtaCopyExperiment is what prevents the *other* double-fire vector, across the
	// two sibling mounts — at most one of them owns the affordance in every layout, so at most one
	// fires.
	useEffect(() => {
		if (trackShareCtaCopyExperiment) {
			void trackShareCtaCopyExposure();
		}
	}, [trackShareCtaCopyExperiment, trackShareCtaCopyExposure]);

	const openModal = useCallback(() => {
		trackMobileSignIntent();
		setIsOpen(true);
	}, [trackMobileSignIntent]);

	const openModalWithoutTracking = useCallback(() => {
		setIsOpen(true);
	}, []);

	const signButton = (
		<Button
			variant="primary"
			onClick={openModal}
			leadingIcon={<SignaturesIcon size="small" />}
			data-qa="mobile-sign-button"
			data-testid="mobile-sign-button"
			data-gtm="ga4-sign-button"
			size="large"
			className="w-full max-w-none"
		>
			<Translate value="corgi.petition_gamma.petition_sign.action" />
		</Button>
	);

	const shareAffordance = (
		<ClientRender>
			{shareCtaPlacementEnabled ? (
				<SignCtaShareAffordance shareAction={shareAction} variation={petitionDetailsShareCtaCopyExperiment.variation} />
			) : null}
		</ClientRender>
	);

	const actionContent = pickActionContent({
		shareAffordance,
		signButton,
	});

	return (
		<>
			{/* we're looking at breakpoints to decide whether to open the modal, which cannot be done server-side */}
			{/* Only auto-open on the non-sticky instance to avoid opening modal twice (both sticky and non-sticky render this component) */}
			{!sticky && (
				<ClientRender>
					<MobileSignButtonAutoOpen onOpen={openModalWithoutTracking} />
				</ClientRender>
			)}
			{/* eslint-disable-next-line @change-corgi/require-data-qa-attribute */}
			<ActionButtonWrapper visible={visible} sticky={sticky}>
				{actionContent}
			</ActionButtonWrapper>

			<MobileSignModal context={context} isOpen={isOpen} setIsOpen={setIsOpen} variant={variant} />
		</>
	);
};
function MobileSignButtonAutoOpen({ onOpen }: { onOpen: () => void }) {
	const showSignForm = useShowSignForm();

	useEffect(() => {
		if (showSignForm) {
			/**
			 * There are some updates that happen right before showing the modal (such as populating the form fields)
			 * that need to complete first so that the modal opens with the correct data.
			 * Using setTimeout to defer the onOpen call to the next event loop iteration and avoid a race condition.
			 */
			setTimeout(() => {
				onOpen();
			}, 0);
		}
		// disabling dependency check to avoid attempting to open the modal multiple times
	}, []);

	return null;
}
